import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import { RotateCcw, X, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type PointerEvent } from "react";

import { fetchAttachmentText } from "@/api/attachments";
import { Spinner } from "@/components/Spinner";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const ZOOM_FACTOR = 1.25;
const ZOOM_MIN = 0.1;

export function AttachmentViewerModal({
  open,
  onClose,
  blob,
  mimeType,
  fileName,
  tabId,
  grievanceId,
  mode = "real",
}: {
  open: boolean;
  onClose: () => void;
  blob: Blob | null;
  mimeType: string;
  fileName: string;
  tabId: string;
  grievanceId: number;
  mode?: "real" | "test";
}) {
  const objectUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);

  // DOCX text-extraction endpoint only exists on the real (govt-DB) path.
  // Test tickets fall back to a "download to open" hint for DOCX.
  const docxTextQuery = useQuery({
    queryKey: ["attachment-text", tabId, grievanceId],
    queryFn: () => fetchAttachmentText(tabId, grievanceId),
    enabled: open && mimeType === DOCX_MIME && mode === "real",
  });

  // Unbounded zoom (users with low vision may want 1000%+). Continuous scale.
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    if (open) setZoom(1);
  }, [open, fileName]);

  const zoomIn = () => setZoom((z) => z * ZOOM_FACTOR);
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, z / ZOOM_FACTOR));
  const zoomReset = () => setZoom(1);

  const isImage = mimeType.startsWith("image/");
  const isPdf = mimeType === "application/pdf";
  const isDocxReal = mimeType === DOCX_MIME && mode === "real";
  const canZoom = isImage || isPdf || isDocxReal;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-5xl max-h-[90vh] flex flex-col bg-white dark:bg-surface-raised rounded-lg shadow-2xl border border-slate-200 dark:border-surface-border overflow-hidden">
          <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-slate-200 dark:border-surface-border">
            <DialogTitle className="text-sm font-semibold truncate">
              Attachment Viewer · {fileName}
            </DialogTitle>
            <div className="flex items-center gap-1">
              {canZoom && (
                <div className="flex items-center gap-0.5 mr-2">
                  <button
                    type="button"
                    onClick={zoomOut}
                    disabled={zoom <= ZOOM_MIN}
                    className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-surface-border disabled:opacity-40 disabled:hover:bg-transparent"
                    aria-label="Zoom out"
                    title="Zoom out"
                  >
                    <ZoomOut size={16} />
                  </button>
                  <span
                    className="tabular-nums text-xs text-slate-500 min-w-[3.25rem] text-center select-none"
                    title={`Current zoom (drag to pan when >100%)`}
                  >
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    type="button"
                    onClick={zoomIn}
                    className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-surface-border"
                    aria-label="Zoom in"
                    title="Zoom in"
                  >
                    <ZoomIn size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={zoomReset}
                    disabled={zoom === 1}
                    className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-surface-border disabled:opacity-40 disabled:hover:bg-transparent"
                    aria-label="Reset zoom"
                    title="Reset zoom"
                  >
                    <RotateCcw size={14} />
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-surface-border"
                aria-label="Close viewer"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          {!objectUrl ? (
            <div className="flex-1 flex items-center justify-center bg-slate-50 dark:bg-surface">
              <Spinner label="Loading attachment…" />
            </div>
          ) : isImage ? (
            <PanZoomStage zoom={zoom}>
              <img
                src={objectUrl}
                alt={fileName}
                draggable={false}
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: "top left",
                }}
                className="block select-none"
              />
            </PanZoomStage>
          ) : isPdf ? (
            // Chrome/Edge PDF viewer honors #zoom=<percent> in the URL fragment;
            // re-mount the iframe on zoom change so the built-in viewer re-renders.
            // Panning is handled by the PDF viewer's own scrollbars.
            <div className="flex-1 overflow-hidden bg-slate-50 dark:bg-surface">
              <iframe
                key={`pdf-${zoom}`}
                src={`${objectUrl}#zoom=${Math.round(zoom * 100)}`}
                title={fileName}
                className="w-full h-full bg-white"
              />
            </div>
          ) : isDocxReal ? (
            <div className="flex-1 overflow-auto bg-slate-50 dark:bg-surface p-5">
              {docxTextQuery.isLoading ? (
                <Spinner label="Extracting DOCX text…" />
              ) : docxTextQuery.isError ? (
                <div className="alert-danger">
                  {(docxTextQuery.error as Error)?.message ?? "Failed to read DOCX."}
                </div>
              ) : (
                <pre
                  style={{ fontSize: `${0.875 * zoom}rem`, lineHeight: 1.55 }}
                  className="whitespace-pre-wrap font-mono"
                >
                  {docxTextQuery.data?.text}
                </pre>
              )}
            </div>
          ) : (
            <div className="flex-1 p-8 bg-slate-50 dark:bg-surface">
              <div className="alert-warning">
                Preview is not available for this file type ({mimeType}). Use the Download button instead.
              </div>
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}

/**
 * Scroll-and-drag container. The child (typically the scaled <img>) sizes
 * itself and this stage panes the viewport around it via scrollLeft/scrollTop.
 * Cursor flips to grab/grabbing so users know they can drag.
 */
function PanZoomStage({
  zoom,
  children,
}: {
  zoom: number;
  children: React.ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startX: number; startY: number; scrollLeft: number; scrollTop: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Recenter the content when zoom changes so the newly-scaled child appears
  // roughly under the previous viewport center, not stuck at the top-left.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Wait for the child to reflow at the new scale.
    const raf = requestAnimationFrame(() => {
      const cx = (el.scrollWidth - el.clientWidth) / 2;
      const cy = (el.scrollHeight - el.clientHeight) / 2;
      if (cx > 0) el.scrollLeft = cx;
      if (cy > 0) el.scrollTop = cy;
    });
    return () => cancelAnimationFrame(raf);
  }, [zoom]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    // Ignore right-click / middle-click and drag-starts on interactive children.
    if (e.button !== 0) return;
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop,
    };
    el.setPointerCapture(e.pointerId);
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = dragState.current;
    const el = scrollRef.current;
    if (!d || !el) return;
    el.scrollLeft = d.scrollLeft - (e.clientX - d.startX);
    el.scrollTop = d.scrollTop - (e.clientY - d.startY);
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    scrollRef.current?.releasePointerCapture(e.pointerId);
    dragState.current = null;
    setDragging(false);
  };

  return (
    <div
      ref={scrollRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className="flex-1 overflow-auto bg-slate-50 dark:bg-surface p-4"
      style={{ cursor: dragging ? "grabbing" : "grab", touchAction: "none" }}
    >
      {children}
    </div>
  );
}
